/**
 * Sistema de Logging Condicional para o UaizyChat
 * 
 * RELEASE: 2026-01-27
 * VERSION: 2.0.0 - Integração com Sentry
 * 
 * Funcionalidades:
 * - Logs só aparecem em desenvolvimento (ou quando VITE_ENABLE_LOGS=true)
 * - Diferentes níveis: debug, info, warn, error
 * - Namespaces para filtrar por contexto (ex: [Auth], [Chat])
 * - Seguro para produção - nenhum dado sensível vazado
 * - NOVO: Erros são automaticamente enviados ao Sentry em produção
 * 
 * Uso:
 * import { createLogger } from '@/lib/logger';
 * const logger = createLogger('MeuModulo');
 * logger.info('Mensagem', { dados: 'opcionais' });
 */

import { captureMessage } from '@/lib/sentry';

// =============================================================================
// CONFIGURAÇÃO DE AMBIENTE
// =============================================================================

/**
 * Verifica se o logging está habilitado baseado no ambiente
 * - Em desenvolvimento (Vite DEV mode): sempre habilitado
 * - Em produção: só se VITE_ENABLE_LOGS=true
 */
const isLoggingEnabled = (): boolean => {
  // Em desenvolvimento, sempre habilita logs
  if (import.meta.env.DEV) return true;
  
  // Em produção, verifica variável de ambiente explícita
  return import.meta.env.VITE_ENABLE_LOGS === 'true';
};

// Cache do estado de logging (evita verificação repetida a cada chamada)
const LOGGING_ENABLED = isLoggingEnabled();

// =============================================================================
// TIPOS E CONSTANTES
// =============================================================================

/** Níveis de log disponíveis */
type LogLevel = 'debug' | 'info' | 'warn' | 'error';

/** Cores para cada nível (melhora visualização no console do navegador) */
const LOG_COLORS: Record<LogLevel, string> = {
  debug: '#9CA3AF', // cinza - informações detalhadas de debug
  info: '#3B82F6',  // azul - informações gerais
  warn: '#F59E0B',  // amarelo - avisos
  error: '#EF4444', // vermelho - erros
};

/** Ícones para cada nível (opcional, melhora identificação visual) */
const LOG_ICONS: Record<LogLevel, string> = {
  debug: '🔍',
  info: 'ℹ️',
  warn: '⚠️',
  error: '❌',
};

// =============================================================================
// FUNÇÃO PRINCIPAL DE LOGGING
// =============================================================================

/**
 * Função interna de logging com formatação padronizada
 * 
 * @param level - Nível do log (debug, info, warn, error)
 * @param namespace - Contexto/módulo de origem (ex: 'Auth', 'Chat', 'Z-API')
 * @param message - Mensagem principal do log
 * @param data - Dados adicionais para log (opcional)
 */
const log = (
  level: LogLevel, 
  namespace: string, 
  message: string, 
  data?: unknown
): void => {
  // Não executa nada se logging está desabilitado (zero overhead em produção)
  if (!LOGGING_ENABLED) return;
  
  // Formata timestamp como HH:MM:SS para facilitar leitura
  const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
  const color = LOG_COLORS[level];
  const icon = LOG_ICONS[level];
  
  // Monta prefixo estilizado: [HH:MM:SS] [Namespace]
  const prefix = `%c${icon} [${timestamp}] [${namespace}]`;
  const style = `color: ${color}; font-weight: bold`;
  
  // Seleciona método do console apropriado para o nível
  const consoleMethod = level === 'error' 
    ? console.error 
    : level === 'warn' 
      ? console.warn 
      : console.log;
  
  // Executa log com ou sem dados adicionais
  if (data !== undefined) {
    consoleMethod(prefix, style, message, data);
  } else {
    consoleMethod(prefix, style, message);
  }
  
  // Envia erros automaticamente para o Sentry em produção
  // Isso garante que todos os erros logados sejam monitorados
  if (level === 'error' && import.meta.env.PROD) {
    captureMessage(`[${namespace}] ${message}`, 'error', {
      tags: { namespace },
      extra: data ? { data } : undefined,
    });
  }
};

// =============================================================================
// API PÚBLICA - LOGGER COM NAMESPACE
// =============================================================================

/**
 * Interface do logger com namespace
 */
interface NamespacedLogger {
  /** Log de debug - informações detalhadas para desenvolvimento */
  debug: (message: string, data?: unknown) => void;
  /** Log de info - informações gerais do fluxo */
  info: (message: string, data?: unknown) => void;
  /** Log de warning - avisos de situações inesperadas */
  warn: (message: string, data?: unknown) => void;
  /** Log de error - erros e exceções */
  error: (message: string, data?: unknown) => void;
}

/**
 * Cria um logger com namespace pré-definido
 * 
 * @param namespace - Nome do módulo/contexto (ex: 'Auth', 'Chat', 'Webhook')
 * @returns Logger com métodos debug, info, warn, error
 * 
 * @example
 * const chatLogger = createLogger('Chat');
 * chatLogger.info('Conversa carregada', { conversationId: '123' });
 * chatLogger.error('Falha ao enviar mensagem', { error: err.message });
 */
export const createLogger = (namespace: string): NamespacedLogger => ({
  debug: (message: string, data?: unknown) => log('debug', namespace, message, data),
  info: (message: string, data?: unknown) => log('info', namespace, message, data),
  warn: (message: string, data?: unknown) => log('warn', namespace, message, data),
  error: (message: string, data?: unknown) => log('error', namespace, message, data),
});

// =============================================================================
// API PÚBLICA - LOGGER GLOBAL
// =============================================================================

/**
 * Logger global para uso rápido sem criar instância
 * Útil para logs esporádicos onde criar um logger seria overhead
 * 
 * @example
 * logger.info('App', 'Aplicação iniciada');
 * logger.error('Network', 'Falha na requisição', { status: 500 });
 */
export const logger = {
  debug: (namespace: string, message: string, data?: unknown) => 
    log('debug', namespace, message, data),
  info: (namespace: string, message: string, data?: unknown) => 
    log('info', namespace, message, data),
  warn: (namespace: string, message: string, data?: unknown) => 
    log('warn', namespace, message, data),
  error: (namespace: string, message: string, data?: unknown) => 
    log('error', namespace, message, data),
};

// =============================================================================
// EXPORTAÇÕES AUXILIARES
// =============================================================================

/** 
 * Indica se o modo de desenvolvimento está ativo
 * Útil para condicionais fora do sistema de logging
 */
export const isDevMode = LOGGING_ENABLED;

/**
 * Verifica se logging está habilitado (para uso externo)
 */
export const isLoggingActive = (): boolean => LOGGING_ENABLED;
